import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";
import { db } from "../firebase";
import {
  collection,
  doc,
  setDoc,
  serverTimestamp,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  Timestamp,
} from "firebase/firestore";
import { invalidatePatternTierCache } from "../hooks/usePatternTier";
import { authedWrite } from "../utils/authedWrite";

// ---------------------------------------------------------------------------
// Orphan-recovery tuning
// ---------------------------------------------------------------------------
// If a session was started within this window and never ended (e.g. the user
// force-quit the app, the browser tab crashed, the OS killed us), we treat it
// as "still your active trip" and rehydrate it. Anything older is presumed
// abandoned and gets auto-closed.
const SESSION_RESUME_WINDOW_MS = 8 * 60 * 60 * 1000; // 8 hours
// When auto-closing an ancient orphan, we set endedAt to startedAt + this
// estimate so duration math doesn't show a 3-day "trip".
const ORPHAN_AUTO_END_OFFSET_MS = 4 * 60 * 60 * 1000; // 4 hours
const ORPHAN_QUERY_LIMIT = 10;

// localStorage key for the in-flight session snapshot. Scoped per-user so
// multiple accounts on the same device don't collide.
const lsKey = (uid) => `the99.activeSession.${uid}`;

function readActiveSessionFromLS(uid) {
  if (!uid || typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(lsKey(uid));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || !parsed.sessionId) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeActiveSessionToLS(uid, snapshot, resumed = false) {
  if (!uid || typeof window === "undefined") return;
  try {
    window.localStorage.setItem(lsKey(uid), JSON.stringify(snapshot));
  } catch {
    /* quota / private-mode — non-fatal */
  }
  // Notify the app shell (which owns the session timer but is mounted ABOVE
  // this provider, so it can't read our state directly). When we recover an
  // orphaned session asynchronously from Firestore, the shell's one-time
  // localStorage read on mount has already run, so without this signal the
  // timer would reset to zero instead of resuming at the true elapsed time.
  // `resumed` tells the shell whether this is a rehydrated previous trip (so
  // it can show a "resumed" label) versus a brand-new session.
  try {
    window.dispatchEvent(
      new CustomEvent("the99:activeSession", {
        detail: { startedAtMs: snapshot?.startedAtMs ?? null, resumed: Boolean(resumed) },
      })
    );
  } catch {
    /* CustomEvent unsupported — non-fatal */
  }
}

function clearActiveSessionFromLS(uid) {
  if (!uid || typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(lsKey(uid));
  } catch {
    /* non-fatal */
  }
}

function toMillis(value) {
  if (!value) return null;
  if (typeof value === "number") return value;
  if (typeof value?.toMillis === "function") return value.toMillis();
  if (typeof value?.seconds === "number") return value.seconds * 1000;
  return null;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------
const SessionContext = createContext(null);

export function SessionProvider({ children, userId: authUserId }) {
  const userId = authUserId || null;
  const [sessionId, setSessionId] = useState(null);
  const [lastEndedSessionId, setLastEndedSessionId] = useState(null);
  const [sessionConditions, setSessionConditions] = useState(null);
  const [sessionLocation, setSessionLocation] = useState(null);
  // Tournament context for the active session (real tournament doc id + team).
  // Carried onto every catch so the director dashboard can discover them via
  // collectionGroup('catches').where('tournamentId','==',tid).
  const [sessionTournament, setSessionTournament] = useState(null);
  const [lastConditionLogAt, setLastConditionLogAt] = useState(null);
  // Id of the most recently logged catch in the active session. Set
  // synchronously the moment `logCatch` succeeds so consumers (e.g. the
  // "Video Proof" button) can attach a video to the just-logged catch without
  // racing the Firestore snapshot — which, for the FIRST catch of a session,
  // can still be empty and would otherwise orphan the video.
  const [lastCatchId, setLastCatchId] = useState(null);
  // Id of an uploaded-and-certified video that was recorded BEFORE its catch
  // existed (e.g. the angler taps "Video Proof" first, certifies, then returns
  // to recording instead of going through VideoScreen's own "Log Catch"
  // button). We stash its certId here and link it to the very next catch logged
  // through ANY path, so the clip never ends up orphaned and missing from the
  // trip summary. A ref (not state) so `logCatch` always sees the latest value
  // without being re-created.
  const pendingVideoCertIdRef = useRef(null);
  // True for one mount after we rehydrate an orphaned session, so the UI can
  // show a "Resumed your previous trip" banner. Consumers should call
  // `acknowledgeResume()` after displaying it.
  const [wasResumed, setWasResumed] = useState(false);
  const recoveryRanForUserRef = useRef(null);

  // ── Orphan recovery on user login / mount ────────────────────────────────
  // Runs once per authenticated userId. Looks for sessions with `endedAt: null`.
  //   • If one is found and is < SESSION_RESUME_WINDOW_MS old → rehydrate it
  //     into in-memory state so the user can keep logging catches against it.
  //   • If it's older → silently set `endedAt` to a reasonable estimate and
  //     flag `autoClosed: true` so we can distinguish forensically.
  //   • If multiple are found → newest one is the resume candidate; everything
  //     older gets auto-closed.
  // Without this, every force-quit produces an orphan session that lingers
  // forever with `endedAt: null` and corrupts duration math / AI inputs.
  useEffect(() => {
    if (!userId) return;
    if (recoveryRanForUserRef.current === userId) return;
    recoveryRanForUserRef.current = userId;

    let cancelled = false;

    (async () => {
      // Fast path: localStorage may already hold the active session from
      // this same browser/tab — rehydrate immediately without waiting on a
      // network round trip.
      const cached = readActiveSessionFromLS(userId);
      if (cached?.sessionId) {
        const ageMs = Date.now() - (cached.startedAtMs || 0);
        if (ageMs >= 0 && ageMs < SESSION_RESUME_WINDOW_MS) {
          if (!cancelled) {
            setSessionId(cached.sessionId);
            setSessionConditions(cached.conditions ?? null);
            setSessionLocation(cached.location ?? null);
            setSessionTournament(cached.tournament ?? null);
            // Surface the resume banner. On native (Capacitor) every app
            // launch is a process restart, and on web a refresh mid-trip is
            // also a recovery worth signalling. Worst case is a benign flash
            // during a dev hot-reload, which the user can dismiss instantly.
            setWasResumed(true);
          }
        } else {
          // Stale cache, drop it; the Firestore query below will handle it.
          clearActiveSessionFromLS(userId);
        }
      }

      try {
        const sessionsRef = collection(db, "users", userId, "sessions");
        const q = query(
          sessionsRef,
          where("endedAt", "==", null),
          orderBy("startedAt", "desc"),
          limit(ORPHAN_QUERY_LIMIT)
        );
        const snap = await getDocs(q);
        if (cancelled || snap.empty) return;

        const docs = snap.docs;
        let resumeCandidate = null;

        for (let i = 0; i < docs.length; i++) {
          const d = docs[i];
          const data = d.data() || {};
          // Prefer the server-side startedAt, fall back to the client-side
          // mirror we write at start time (handles the brief window before
          // serverTimestamp() resolves).
          const startedAtMs = toMillis(data.startedAt) ?? data.clientStartedAtMs ?? null;
          const ageMs = startedAtMs ? Date.now() - startedAtMs : Number.POSITIVE_INFINITY;

          if (i === 0 && ageMs < SESSION_RESUME_WINDOW_MS) {
            // Newest orphan is fresh enough to resume.
            resumeCandidate = { id: d.id, data, startedAtMs };
            continue;
          }
          // Auto-close everything else.
          try {
            const endedAtEstimateMs =
              (startedAtMs ?? Date.now()) + ORPHAN_AUTO_END_OFFSET_MS;
            await setDoc(
              doc(db, "users", userId, "sessions", d.id),
              {
                endedAt: Timestamp.fromMillis(Math.min(endedAtEstimateMs, Date.now())),
                autoClosed: true,
                autoClosedReason: "orphaned_session_recovery",
                autoClosedAt: serverTimestamp(),
              },
              { merge: true }
            );
            console.log("[Session] auto-closed orphan →", d.id);
          } catch (err) {
            console.error("[Session] failed to auto-close orphan", d.id, err);
          }
        }

        if (cancelled) return;

        if (resumeCandidate && resumeCandidate.id !== cached?.sessionId) {
          // We found a fresh orphan that wasn't in our LS cache — that means
          // the previous app instance died before we could persist it, or
          // the user is on a different device/browser. Rehydrate and flag a
          // banner so the user knows.
          const { id, data } = resumeCandidate;
          setSessionId(id);
          setSessionConditions(data.conditions ?? null);
          setSessionLocation(data.location ?? null);
          setSessionTournament({
            tournamentId: data.tournamentId ?? null,
            tournamentName: data.tournamentName ?? null,
            teamId: data.teamId ?? null,
            teamName: data.teamName ?? null,
          });
          setWasResumed(true);
          writeActiveSessionToLS(userId, {
            sessionId: id,
            startedAtMs: resumeCandidate.startedAtMs ?? Date.now(),
            conditions: data.conditions ?? null,
            location: data.location ?? null,
            tournament: {
              tournamentId: data.tournamentId ?? null,
              tournamentName: data.tournamentName ?? null,
              teamId: data.teamId ?? null,
              teamName: data.teamName ?? null,
            },
          }, true);
          console.log("[Session] resumed orphan →", id);
        } else if (!resumeCandidate && cached?.sessionId) {
          // LS thought we had a session but Firestore disagrees (it was
          // closed elsewhere). Clear the cache so we don't write catches to
          // a dead session.
          clearActiveSessionFromLS(userId);
          setSessionId(null);
          setSessionConditions(null);
          setSessionLocation(null);
          setSessionTournament(null);
        }
      } catch (err) {
        console.error("[Session] orphan-recovery query failed:", err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [userId]);

  // Reset recovery guard when the user logs out so a fresh login re-runs it.
  useEffect(() => {
    if (!userId) {
      recoveryRanForUserRef.current = null;
      setSessionId(null);
      setSessionConditions(null);
      setSessionLocation(null);
      setSessionTournament(null);
      setLastConditionLogAt(null);
      setWasResumed(false);
    }
  }, [userId]);

  const acknowledgeResume = useCallback(() => setWasResumed(false), []);

  // ── Start a new fishing session ──────────────────────────────────────────
  // `tideContext` (optional): { tideStationId, tideStationName }
  //   Persisted at the top level of the session doc (not nested in
  //   `conditions`) so AI pattern queries can filter sessions by station
  //   with a single equality clause:
  //     where('tideStationId', '==', target)
  const startSession = useCallback(
    async (conditionsSnapshot, location, tournamentContext = null, tideContext = null, prepContext = null) => {
      if (!userId) {
        console.error("[Session] refusing to start — no authenticated user yet");
        return null;
      }
      const clientStartedAtMs = Date.now();
      // Snapshot the Preparation Score shown on the dashboard at the moment the
      // trip starts. The score depends on live conditions + the user's evolving
      // catch history, so it can't be faithfully recomputed later — capturing it
      // here lets the trip report compare the prediction against the outcome.
      const prepScoreSnapshot =
        prepContext && Number.isFinite(Number(prepContext.score)) && Number(prepContext.score) > 0
          ? {
              score: Number(prepContext.score),
              confidence: prepContext.confidence ?? null,
              capturedAt: serverTimestamp(),
            }
          : null;
      try {
        const sessRef = doc(collection(db, "users", userId, "sessions"));
        await authedWrite(() =>
          setDoc(sessRef, {
            startedAt: serverTimestamp(),
            clientStartedAtMs,
            endedAt: null,
            tournamentId: tournamentContext?.tournamentId ?? null,
            tournamentName: tournamentContext?.tournamentName ?? null,
            teamId: tournamentContext?.teamId ?? null,
            teamName: tournamentContext?.teamName ?? null,
            tideStationId: tideContext?.tideStationId ?? null,
            tideStationName: tideContext?.tideStationName ?? null,
            // Phase 1 prefishing: when set, this pre-tournament scouting trip
            // is tagged with the tournament id it's prefishing for, so a later
            // scouting/strategy report can aggregate these sessions.
            prefishFor: tideContext?.prefishFor ?? null,
            location: location ? { lat: location.lat, lng: location.lng } : null,
            prepScore: prepScoreSnapshot,
            conditions: {
              ...conditionsSnapshot,
              capturedAt: serverTimestamp(),
            },
          })
        );
        setSessionId(sessRef.id);
        setLastEndedSessionId(null);
        setSessionConditions(conditionsSnapshot);
        setSessionLocation(location ?? null);
        setSessionTournament(tournamentContext || null);
        setLastConditionLogAt(null);
        setLastCatchId(null);
        pendingVideoCertIdRef.current = null;
        setWasResumed(false);
        writeActiveSessionToLS(userId, {
          sessionId: sessRef.id,
          startedAtMs: clientStartedAtMs,
          conditions: conditionsSnapshot ?? null,
          location: location ?? null,
          tournament: tournamentContext || null,
        });
        console.log("[Session] started →", sessRef.id);
        return sessRef.id;
      } catch (err) {
        console.error("[Session] failed to start:", err);
        return null;
      }
    },
    [userId]
  );

  // ── End the active session ───────────────────────────────────────────────
  const endSession = useCallback(async (options = null) => {
    if (!sessionId) return;
    const endingSessionId = sessionId;
    try {
      const sessRef = doc(db, "users", userId, "sessions", sessionId);
      const payload = { endedAt: serverTimestamp() };
      if (options?.exportEmail) {
        payload.exportEmail = {
          shouldSend: Boolean(options.exportEmail.shouldSend),
          userEmail: options.exportEmail.userEmail ?? null,
          isTournamentSession: Boolean(options.exportEmail.isTournamentSession),
          tournamentId: options.exportEmail.tournamentId ?? null,
          tournamentName: options.exportEmail.tournamentName ?? null,
          teamId: options.exportEmail.teamId ?? null,
          teamName: options.exportEmail.teamName ?? null,
          capturedAt: serverTimestamp(),
        };
      }
      await authedWrite(() => setDoc(sessRef, payload, { merge: true }));
      console.log("[Session] ended →", sessionId);
      // Recompute the pattern tier on next dashboard mount — this trip may have
      // crossed a catch-session threshold (and offline catches may have synced).
      invalidatePatternTierCache(userId);
      setLastEndedSessionId(endingSessionId);
    } catch (err) {
      console.error("[Session] failed to end:", err);
    } finally {
      clearActiveSessionFromLS(userId);
      setSessionId(null);
      setSessionConditions(null);
      setSessionLocation(null);
      setSessionTournament(null);
      setLastConditionLogAt(null);
      setLastCatchId(null);
      pendingVideoCertIdRef.current = null;
      setWasResumed(false);
    }
    return endingSessionId;
  }, [sessionId, userId]);

  // ── Register an uploaded video that hasn't been linked to a catch yet ────
  // Called by VideoScreen after a successful upload+certify when no catchId was
  // available. The next logCatch() (through any UI path) will link it.
  const registerPendingVideo = useCallback((certId) => {
    pendingVideoCertIdRef.current = certId || null;
  }, []);

  // ── Log a catch under the active session ────────────────────────────────
  // `catchData` shape: { species, lengthIn, weightLbs, lure, waterClarity, transcript, outcome }
  // `outcome` is "landed" (default) or "missed" (a bite that got away).
  // Conditions snapshot defaults to session-start conditions if not overridden.
  const logCatch = useCallback(
    async (catchData, conditionsOverride, locationOverride) => {
      if (!userId) {
        throw new Error("You must be signed in to log a catch.");
      }
      if (!sessionId) {
        throw new Error("No active session. Start a trip before logging catches.");
      }
      try {
        const catchRef = doc(
          collection(db, "users", userId, "sessions", sessionId, "catches")
        );
        const loc = locationOverride ?? sessionLocation;
        const conds = conditionsOverride ?? sessionConditions;
        await authedWrite(() =>
          setDoc(catchRef, {
            sessionId,
            caughtAt: serverTimestamp(),
            // "landed" = a fish brought to hand; "missed" = a bite/strike that
            // got away. Missed fish carry the same species/lure/bait/clarity
            // context but no length or weight, and are excluded from catch
            // counts, patterns, and leaderboard scoring.
            outcome: catchData.outcome === "missed" ? "missed" : "landed",
            species: catchData.species ?? null,
            lengthIn: catchData.lengthIn ?? null,
            weightLbs: catchData.weightLbs ?? null,
            lure: catchData.lure ?? null,
            liveBait: catchData.liveBait ?? null,
            waterClarity: catchData.waterClarity ?? null,
            transcript: catchData.transcript ?? null,
            location: loc ? { lat: loc.lat, lng: loc.lng } : null,
            conditionsSnapshot: conds
              ? { ...conds, capturedAt: serverTimestamp() }
              : null,
            // Tournament fields so the director dashboard can discover this
            // catch via collectionGroup('catches').where('tournamentId','==',tid).
            // Sourced from the active session's real tournament doc id.
            tournamentId: sessionTournament?.tournamentId ?? null,
            tournamentName: sessionTournament?.tournamentName ?? null,
            teamId: sessionTournament?.teamId ?? null,
            teamName: sessionTournament?.teamName ?? null,
            reviewStatus: "pending",
          })
        );
        console.log("[Session] catch logged →", catchRef.id);
        // Remember the just-logged catch so a video proof recorded immediately
        // afterwards can link to it even before the catches snapshot updates.
        setLastCatchId(catchRef.id);
        // Link any uploaded-but-unattached video proof to this catch so it
        // surfaces in the trip summary. Without this, a clip recorded before
        // the catch was logged (and not finished via VideoScreen's own "Log
        // Catch" button) would stay orphaned and appear "not uploaded".
        const pendingCert = pendingVideoCertIdRef.current;
        if (pendingCert) {
          pendingVideoCertIdRef.current = null;
          const linkRef = { userId, sessionId, catchId: catchRef.id };
          try {
            await authedWrite(() =>
              setDoc(
                doc(db, "certificates", pendingCert),
                { catchRef: linkRef },
                { merge: true }
              )
            );
            await authedWrite(() =>
              setDoc(
                doc(db, "videos", pendingCert),
                { catchRef: linkRef },
                { merge: true }
              )
            );
            await authedWrite(() =>
              setDoc(
                catchRef,
                {
                  status: "certified",
                  videoCertified: true,
                  certificateId: pendingCert,
                  certifiedAt: serverTimestamp(),
                },
                { merge: true }
              )
            );
            console.log("[Session] linked pending video → catch", catchRef.id);
          } catch (linkErr) {
            console.error("[Session] failed to link pending video to catch:", linkErr);
          }
        }
        // The pattern-tier progress pill is keyed off catch-sessions; drop its
        // cached value so the dashboard recomputes (e.g. 0/1 → 1/1) instead of
        // waiting out the 24h TTL.
        invalidatePatternTierCache(userId);
        return catchRef.id;
      } catch (err) {
        console.error("[Session] failed to log catch:", err);
        throw err;
      }
    },
    [sessionId, sessionConditions, sessionLocation, sessionTournament, userId]
  );

  // ── Log periodic conditions under the active session ─────────────────────
  const logConditionSnapshot = useCallback(
    async (conditionsSnapshot, locationOverride) => {
      if (!sessionId || !conditionsSnapshot) return null;
      if (!userId) {
        console.warn("[Session] skipping condition log — no authenticated user yet");
        return null;
      }
      try {
        const timestampId = `${Date.now()}`;
        const condRef = doc(
          db,
          "users",
          userId,
          "sessions",
          sessionId,
          "conditionLog",
          timestampId
        );
        const loc = locationOverride ?? sessionLocation;
        await authedWrite(
          () =>
            setDoc(condRef, {
              capturedAt: serverTimestamp(),
              clientCapturedAtMs: Date.now(),
              location: loc ? { lat: loc.lat, lng: loc.lng } : null,
              conditions: conditionsSnapshot,
            }),
          `conditionLog users/${userId}/sessions/${sessionId}`
        );
        setLastConditionLogAt(Date.now());
        return timestampId;
      } catch (err) {
        console.error("[Session] failed to log conditions:", err);
        return null;
      }
    },
    [sessionId, sessionLocation, userId]
  );

  return (
    <SessionContext.Provider
      value={{
        userId,
        sessionId,
        lastEndedSessionId,
        sessionConditions,
        sessionLocation,
        lastConditionLogAt,
        lastCatchId,
        wasResumed,
        acknowledgeResume,
        startSession,
        endSession,
        logCatch,
        registerPendingVideo,
        logConditionSnapshot,
      }}
    >
      {children}
    </SessionContext.Provider>
  );
}

export function useSessionContext() {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error("useSessionContext must be used inside <SessionProvider>");
  return ctx;
}
